/**
 * Lucide Icons Registry
 *
 * Bridges the existing iconRegistry (Lucide icons) with the new
 * registry pattern. Also handles legacy icon identifiers.
 */

import type { ReactNode } from 'react';

import {
  getIconById,
  iconRegistry,
} from '@/sections/attacks/attackPaths/components/icons/iconRegistry';
import type {
  IconDefinition,
  IconRenderer,
  IconRenderProps,
} from '@/sections/attacks/attackPaths/components/icons/registry/icon-renderers-map';
import { iconRenderers } from '@/sections/attacks/attackPaths/components/icons/registry/icon-renderers-map';

// ============================================================================
// Build Icon Map from Existing Registry
// ============================================================================

/**
 * Map of icon ID to icon definition for O(1) lookup.
 */
const iconMap = new Map(iconRegistry.map(def => [def.id, def]));

// ============================================================================
// Lucide Renderer Implementation
// ============================================================================

const lucideRenderer: IconRenderer = {
  render: (name: string, { size, className }: IconRenderProps): ReactNode => {
    const def = iconMap.get(name);
    if (!def) return null;
    const Icon = def.component;
    return <Icon size={size} className={className} />;
  },

  validate: (name: string): boolean => iconMap.has(name),

  getIcon: (name: string) => {
    const def = iconMap.get(name);
    if (!def) return null;
    // Return a wrapper component that accepts size and className
    const Icon = def.component;
    return function WrappedIcon({
      size,
      className,
    }: {
      size?: number;
      className?: string;
    }) {
      return <Icon size={size} className={className} />;
    };
  },

  getAll: (): IconDefinition[] =>
    iconRegistry.map(def => ({
      id: def.id ? `lucide:${def.id}` : '', // Empty id stays empty (default)
      component: def.component as IconDefinition['component'],
      label: def.label,
      category: def.category,
    })),
};

// ============================================================================
// Legacy Renderer Implementation
// ============================================================================

/**
 * Legacy renderer for backwards compatibility.
 *
 * Handles icon identifiers without a source prefix (e.g., 'Server' instead of 'lucide:server').
 * Uses the same icon map as the lucide renderer.
 */
const legacyRenderer: IconRenderer = {
  render: (name: string, { size, className }: IconRenderProps): ReactNode => {
    const Icon = getIconById(name);
    return <Icon size={size} className={className} />;
  },

  validate: (name: string): boolean => iconMap.has(name) || name === '',

  getIcon: (name: string) => {
    const def = iconMap.get(name);
    if (!def && name !== '') return null;
    const Icon = getIconById(name);
    return function WrappedIcon({
      size,
      className,
    }: {
      size?: number;
      className?: string;
    }) {
      return <Icon size={size} className={className} />;
    };
  },

  getAll: (): IconDefinition[] => [], // Legacy renderer only for backwards compat, don't populate picker
};

// ============================================================================
// Register Renderers
// ============================================================================

iconRenderers.set('lucide', lucideRenderer);
iconRenderers.set('legacy', legacyRenderer);

export { legacyRenderer, lucideRenderer };
